/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/**
 * The app's auth cookie pair, and the only readable half of it.
 *
 * `mexusers_authn_token` is HttpOnly, so JS can never see it. The native app
 * injects its `_jti` twin alongside it and that one IS readable, which makes the
 * jti's presence the best available proxy for "did the token cookie arrive" —
 * the same probe mex-mobile-web runs.
 *
 * Deliberately ZERO imports. `utils/storage` reads this to decide the auth
 * transport, and storage is imported by nearly every module in the app, so
 * anything pulled in here would land in almost every screen's module graph and
 * reorder init. Keeping it a leaf is what lets both `storage` and
 * `webviewTelemetry` share it without that cost.
 */
export const AUTHN_TOKEN_JTI_COOKIE = "mexusers_authn_token_jti"

/**
 * The cookies `document.cookie` will admit to, trimmed and non-empty.
 *
 * A count of 0 is meaningful to callers: `document.cookie` only ever exposes
 * non-HttpOnly cookies, so if it shows nothing at all (blocked, partitioned,
 * opaque origin) then an absent jti proves nothing about whether the app
 * injected. Absent *alongside* other readable cookies is the real evidence.
 */
export const readReadableCookies = (): string[] => {
  // The getter itself throws SecurityError on an opaque origin, so a typeof
  // check is not a sufficient guard. Nothing here may take the app down: this
  // runs on the auth path and on the shell's mount path.
  let rawCookies = ""
  try {
    rawCookies = document.cookie || ""
  } catch {
    rawCookies = ""
  }

  return rawCookies
    .split(";")
    .map(cookie => cookie.trim())
    .filter(Boolean)
}

/**
 * The jti's VALUE, for joining a client-side session to the backend's record of
 * the same token. Empty string when absent or unreadable.
 *
 * It cannot authenticate: its HttpOnly twin `mexusers_authn_token` is the
 * credential, is unreadable here by design, and must never be logged. But do
 * not read that as "harmless" — `utils/csrf` mirrors THIS value into
 * `X-CSRF-Token`, so it is also this app's anti-CSRF token, which the migration
 * runbook's own "the jti is opaque" note does not account for (mex-mobile-web
 * does not reuse it that way). Logging it in full is a deliberate call by the
 * MR author on !2823: Datadog retains it 14 days, Scribe is internal, and the
 * cookie is short-lived, so the exposure is bounded and a leaked value is only
 * usable if CORS is also misconfigured. Weigh that again before widening where
 * this goes.
 */
export const readAuthnTokenJti = (
  cookies: string[] = readReadableCookies(),
): string => {
  const prefix = `${AUTHN_TOKEN_JTI_COOKIE}=`
  const match = cookies.find(cookie => cookie.startsWith(prefix))
  if (match === undefined) return ""

  try {
    return decodeURIComponent(match.slice(prefix.length))
  } catch {
    // A malformed percent-escape throws. The presence reading is the half that
    // gates auth, so it must survive a value this cannot decode.
    return ""
  }
}

/**
 * Whether the app injected the auth cookie pair.
 *
 * Presence of the COOKIE, not of a value: a bare `mexusers_authn_token_jti=`
 * still means injection happened and the value was merely cleared. The trailing
 * "=" is what makes this an exact-name match rather than a prefix one — without
 * it a differently-named cookie sharing this stem
 * (`mexusers_authn_token_jti_v2`) would report as the real thing.
 *
 * This is the TELEMETRY reading. The auth transport needs a non-empty value
 * instead — see `readAuthnTokenJti` and `isCookieAuthEnabled`.
 */
export const isAuthnTokenJtiPresent = (
  cookies: string[] = readReadableCookies(),
): boolean => {
  const prefix = `${AUTHN_TOKEN_JTI_COOKIE}=`
  return cookies.some(cookie => cookie.startsWith(prefix))
}
